import apiClient from '../api';
import { CoachPlanFile } from '../../types';

export const planFileService = {
  /**
   * Coach uploads a plan file for a specific athlete.
   * Requires an active relationship at the time of upload.
   */
  uploadFile: async (
    athleteUserId: string,
    file: File,
    description?: string
  ): Promise<CoachPlanFile> => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ athleteUserId });
    if (description) params.set('description', description);
    const response = await apiClient.post(`/coach/plan-files?${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Coach lists all plan files they have shared with a specific athlete.
   */
  listForAthlete: async (athleteUserId: string): Promise<CoachPlanFile[]> => {
    const response = await apiClient.get(`/coach/plan-files/${athleteUserId}`);
    return response.data;
  },

  /**
   * Coach deletes a plan file they uploaded.
   */
  deleteFile: async (planFileId: string): Promise<void> => {
    await apiClient.delete(`/coach/plan-files/${planFileId}`);
  },

  /**
   * Athlete lists all plan files shared with them across all coaches.
   * Accessible regardless of current relationship status.
   */
  listMine: async (): Promise<CoachPlanFile[]> => {
    const response = await apiClient.get('/plan-files');
    return response.data;
  },

  /**
   * Download a plan file. Works for both the uploading coach and the target athlete.
   * Triggers a browser file download via a temporary object URL.
   */
  downloadFile: async (planFileId: string, fileName: string): Promise<void> => {
    const response = await apiClient.get(`/plan-files/${planFileId}/content`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};
